import { useState, useEffect } from "react";
import _ from "lodash";
import { DialogClose, DialogContent } from "components/Dialog";
import { Button, ButtonColor, ButtonFill } from "components/Button";
import { Select } from "components/Select";
import {
  ConditionGroupBuilder,
  OptionalNote,
  emptyGroup,
} from "../search/builder";
import {
  ConditionGroup,
  Auth0AccountRole,
  CRMVisualizationColumnCreate,
  CRMVisualizationColumnUpdate,
  CRMVisualizationResponse,
  CRMVisualizationColumnResponse,
  OrganizationDataKeyResponse,
} from "client/openapi";
import { VisualizationsService } from "client/openapi";
import Notifications from "util/notifications";
import {
  isMultiEntityQueryValid,
  isSingleEntityQueryValid,
  withoutBlankSide,
} from "../search/validation";
import { FormOption, SubjectOption } from "../search/editor";
import {
  PERSON_TITLES,
  PersonKind,
  ROLE_OF,
  SEARCH_PEOPLE,
  SEARCH_TYPE_OPTIONS,
  SearchType,
  kindOf,
  searchTypeOf,
} from "../search/searchTypes";

/**
 * A column's stored rule: the conditions on its own people and, for a
 * combined search, the conditions at least one of their linked people meets
 * (`related`). Rules saved before combined searches have no `related`.
 */
type ColumnRule = ConditionGroup & {
  related?: { person_type: Auth0AccountRole; criteria: ConditionGroup };
};

/** A side as the builder shows it: a blank side gets one empty row. */
function shown(group?: ConditionGroup): ConditionGroup {
  if (!group) return emptyGroup();
  return group.conditions.length
    ? group
    : { ...group, conditions: emptyGroup().conditions };
}

/**
 * The column's search and its two sides, or, for a new column, the board's
 * first column's kind of person with nothing picked.
 */
function startingSearch(
  visualization: CRMVisualizationResponse,
  existingColumn?: CRMVisualizationColumnResponse
): { type: SearchType; own: ConditionGroup; linked: ConditionGroup } {
  const { related, ...own } = (existingColumn?.query ?? {}) as ColumnRule;
  const kind = kindOf(
    existingColumn?.column_type ?? visualization.columns[0]?.column_type
  );
  return {
    type: searchTypeOf(kind, related ? kindOf(related.person_type) : null),
    own: existingColumn ? shown(own as ConditionGroup) : emptyGroup(),
    linked: shown(related?.criteria),
  };
}

/** The rule a column saves: as today, plus a combined search's other side. */
function columnRule(
  type: SearchType,
  own: ConditionGroup,
  linked: ConditionGroup
): ColumnRule {
  const filteredBy = SEARCH_PEOPLE[type][1];
  if (!filteredBy) return own;
  return {
    ...withoutBlankSide(own),
    related: {
      person_type: ROLE_OF[filteredBy],
      criteria: withoutBlankSide(linked),
    },
  };
}

export default function AddOrEditColumnDialog({
  visualization,
  existingColumn,
  onColumnAdded,
  onColumnUpdated,
  studentKeys,
  parentKeys,
  tutorKeys,
  subjects,
  forms,
  setIsOpen,
}: {
  visualization: CRMVisualizationResponse;
  existingColumn?: CRMVisualizationColumnResponse;
  onColumnAdded: (column: CRMVisualizationColumnResponse) => void;
  onColumnUpdated: (column: CRMVisualizationColumnResponse) => void;
  studentKeys: OrganizationDataKeyResponse[];
  parentKeys: OrganizationDataKeyResponse[];
  tutorKeys: OrganizationDataKeyResponse[];
  subjects: SubjectOption[];
  forms: FormOption[];
  setIsOpen: (open: boolean) => void;
}) {
  const [name, setName] = useState(existingColumn?.name || "");
  const [position, setPosition] = useState(
    existingColumn?.order_index || visualization.columns.length + 1
  );
  // Search's pattern: who the column holds (A) and, for a combined search,
  // whose conditions filter them (B). Either side of a combined one may be
  // left blank.
  const [start] = useState(() => startingSearch(visualization, existingColumn));
  const [searchType, setSearchType] = useState<SearchType>(start.type);
  const [condGroup, setCondGroup] = useState<ConditionGroup>(start.own);
  const [linkedGroup, setLinkedGroup] = useState<ConditionGroup>(start.linked);
  const [kindA, kindB] = SEARCH_PEOPLE[searchType];
  const columnType = ROLE_OF[kindA];
  const keysFor = (kind: PersonKind) =>
    kind === "parents"
      ? parentKeys
      : kind === "tutors"
      ? tutorKeys
      : studentKeys;
  const rule = columnRule(searchType, condGroup, linkedGroup);
  const isRuleValid = kindB
    ? isMultiEntityQueryValid(
        condGroup,
        linkedGroup,
        keysFor(kindA),
        keysFor(kindB)
      )
    : isSingleEntityQueryValid(condGroup, keysFor(kindA));

  const [isFormDisabled, setIsFormDisabled] = useState(false);

  useEffect(() => {
    const search = startingSearch(visualization, existingColumn);
    setName(existingColumn?.name ?? "");
    setPosition(
      existingColumn?.order_index ?? visualization.columns.length + 1
    );
    setSearchType(search.type);
    setCondGroup(search.own);
    setLinkedGroup(search.linked);
  }, [visualization, existingColumn]);

  const handleSubmit = async () => {
    try {
      if (!name.trim()) {
        return Notifications.error("Column name is required");
      }
      if (!isRuleValid) {
        return Notifications.error("Please fill in all required query fields");
      }
      if (existingColumn) {
        const updateData: CRMVisualizationColumnUpdate = {
          name: name.trim(),
          order_index: position,
          column_type: columnType,
          query: rule,
        };
        VisualizationsService.updateVisualizationColumn({
          visualizationId: visualization.id,
          columnId: existingColumn.id,
          requestBody: updateData,
        })
          .then((updatedColumn) => {
            onColumnUpdated(updatedColumn);
            Notifications.success(
              `Column "${existingColumn.name}" updated successfully!`
            );
          })
          .catch((error) => {
            console.error("Error updating column:", error);
            Notifications.error(
              error?.body?.error ??
                error?.body?.detail ??
                `Could not update column ${existingColumn.name}`
            );
          });
      } else {
        const createData: CRMVisualizationColumnCreate = {
          visualization_id: visualization.id,
          name: name.trim(),
          order_index: position,
          query: rule,
          column_type: columnType,
        };
        VisualizationsService.createVisualizationColumn({
          visualizationId: visualization.id,
          requestBody: createData,
        })
          .then((newColumn) => {
            onColumnAdded(newColumn);
            Notifications.success(
              `Column "${name.trim()}" created successfully!`
            );
          })
          .catch((error) => {
            console.error("Error creating column:", error);
            Notifications.error(
              error?.body?.error ??
                error?.body?.detail ??
                `Could not create column ${name.trim()}`
            );
          });
      }
      setIsOpen(false);
    } catch (error) {
      console.error("Error saving column:", error);
      Notifications.error(
        `Failed to ${existingColumn ? "update" : "create"} column`
      );
    }
  };

  function computeDisabled(): boolean {
    const hasName = name.trim().length > 0;
    const hasValidPosition = position > 0;
    const hasValidQuery = isRuleValid;
    if (position < 1) {
      Notifications.error("Position must be at least 1");
      return true;
    }
    if (existingColumn && position > visualization.columns.length) {
      Notifications.error(
        `Position cannot be greater than ${visualization.columns.length}`
      );
      return true;
    }

    if (!existingColumn && position > visualization.columns.length + 1) {
      Notifications.error(
        `Position cannot be greater than ${visualization.columns.length + 1}`
      );
      return true;
    }
    if (!hasName || !hasValidPosition || !hasValidQuery) {
      return true;
    }
    if (existingColumn) {
      const nameChanged = name.trim() !== existingColumn.name;
      const positionChanged = position !== existingColumn.order_index;
      const typeChanged = columnType !== existingColumn.column_type;
      const queryChanged = !_.isEqual(rule, existingColumn.query);
      return !nameChanged && !positionChanged && !typeChanged && !queryChanged;
    }
    return false;
  }

  useEffect(() => {
    setIsFormDisabled(computeDisabled());
  }, [name, position, searchType, condGroup, linkedGroup, existingColumn]);

  return (
    <DialogContent className="visualization-column-dialog-content">
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">
          {existingColumn ? "Edit Column" : "Add Column"}
        </h2>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Column Name
              </label>
              <input
                type="text"
                className="input w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter column name"
              />
            </div>
            <div className="w-16">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <input
                type="number"
                className="input w-full"
                value={position}
                onChange={(e) => setPosition(parseInt(e.target.value))}
                placeholder="Enter position"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Query Type
            </label>
            <Select
              value={
                SEARCH_TYPE_OPTIONS.find((opt) => opt.value === searchType) ||
                null
              }
              onChange={(opt) => {
                if (opt && opt.value !== searchType) {
                  const [nextA, nextB] = SEARCH_PEOPLE[opt.value];
                  // Fields belong to one kind of person, so start a side
                  // over only when its kind changes.
                  setSearchType(opt.value);
                  if (nextA !== kindA) setCondGroup(emptyGroup());
                  if (nextB !== kindB) setLinkedGroup(emptyGroup());
                }
              }}
              options={SEARCH_TYPE_OPTIONS}
              isClearable={false}
            />
          </div>
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {kindB ? (
                <>
                  {PERSON_TITLES[kindA]}
                  <OptionalNote />
                </>
              ) : (
                "Query Conditions"
              )}
            </label>
            <ConditionGroupBuilder
              value={condGroup}
              onChange={setCondGroup}
              availableKeys={keysFor(kindA)}
              personType={columnType}
              subjects={subjects}
              forms={forms}
            />
          </div>
          {kindB && (
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {PERSON_TITLES[kindB]}
                <OptionalNote />
              </label>
              <ConditionGroupBuilder
                value={linkedGroup}
                onChange={setLinkedGroup}
                availableKeys={keysFor(kindB)}
                personType={ROLE_OF[kindB]}
                subjects={subjects}
                forms={forms}
              />
            </div>
          )}
        </div>
        <div className="flex justify-center gap-2 mt-4">
          <Button
            color={isFormDisabled ? ButtonColor.GRAY : ButtonColor.PURPLE}
            onClick={handleSubmit}
            disabled={isFormDisabled}
          >
            {existingColumn ? "Update" : "Add"}
          </Button>
          <DialogClose>
            <Button
              fill={ButtonFill.HOLLOW}
              color={ButtonColor.BLACK}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
          </DialogClose>
        </div>
      </div>
    </DialogContent>
  );
}
